import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCollectorReconciliation,
  registerCollectorReconciliation,
  getCollectorReconciliationHistory,
} from '@/services/ops.service';
import type {
  DrillDownSelection,
  CollectorReconciliationData,
  RegisterReconciliationRequest,
} from '@/types/collector-reconciliation.types';
import { COLLECTOR_MOCK_DATA } from '@/features/reconciliation/mock/collector-mock-data';

const CACHE_KEY = 'collector-reconciliation';
const HISTORY_KEY = 'collector-reconciliation-history';

const USE_MOCK = false;

export function useCollectorReconciliation() {
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [drillDown, setDrillDown] = useState<DrillDownSelection | null>(null);

  // ─── Queries ────────────────────────────────────────────────────────────────

  const reconciliationQuery = useQuery({
    queryKey: [CACHE_KEY, year],
    queryFn: async () => {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 600));
        return { data: { ...COLLECTOR_MOCK_DATA, resumen: { ...COLLECTOR_MOCK_DATA.resumen, year } } };
      }
      return getCollectorReconciliation(year);
    },
    staleTime: 5 * 60 * 1000,
  });

  const historyQuery = useQuery({
    queryKey: [HISTORY_KEY, year],
    queryFn: async () => {
      if (USE_MOCK) return { data: [] as never[] };
      return getCollectorReconciliationHistory(year);
    },
    staleTime: 5 * 60 * 1000,
  });

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const registerMutation = useMutation({
    mutationFn: (req: RegisterReconciliationRequest) => registerCollectorReconciliation(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [HISTORY_KEY, year] });
    },
  });

  // ─── Derived data ──────────────────────────────────────────────────────────

  const data: CollectorReconciliationData | null = useMemo(() => {
    return (reconciliationQuery.data?.data as CollectorReconciliationData | undefined) ?? null;
  }, [reconciliationQuery.data]);

  const monthData = useMemo(() => {
    return data?.meses ?? null;
  }, [data]);

  const drillDownItems = useMemo(() => {
    if (!drillDown || !monthData) return [];
    const month = monthData[String(drillDown.month)];
    if (!month?.detalles) return [];
    return (month.detalles as Record<string, unknown[]>)[drillDown.statusKey] ?? [];
  }, [drillDown, monthData]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const runReconciliation = useCallback(() => {
    reconciliationQuery.refetch();
  }, [reconciliationQuery]);

  const handleCellClick = useCallback((month: number, statusKey: string) => {
    setDrillDown({ month, statusKey });
  }, []);

  const closeDrillDown = useCallback(() => {
    setDrillDown(null);
  }, []);

  return {
    // State
    year,
    setYear,
    drillDown,

    // Data
    data,
    resumen: data?.resumen ?? null,
    monthData,
    drillDownItems,
    history: historyQuery.data?.data ?? [],

    // Status
    isLoading: reconciliationQuery.isFetching,
    isError: reconciliationQuery.isError,
    hasData: data !== null,
    isRegistering: registerMutation.isPending,

    // Actions
    runReconciliation,
    handleCellClick,
    closeDrillDown,
    register: registerMutation.mutate,
    refetch: reconciliationQuery.refetch,
  };
}
